import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import moment from "moment";
import { RootState } from "../../store";
import { User } from "../../model/User";
import { approveUserAction, denyUserAction } from "./userActions";
import { UserStatus } from "../../model/UserStatus";
import * as authSelectors from "../auth/authSelectors";
import { routes } from "../../appRoutingHelpers";
import * as organizationSelectors from "../organization/organizationSelectors";
import * as userSelectors from "./userSelectors";
import { TestIdsEnum } from "../../TestIdsEnum";
import { TableRow } from "../../components/table-components/TableRow";
import { TableCellLink } from "../../components/table-components/TableCellLink";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { PrimaryButton } from "../../components/Buttons";

interface Props {
  user: User;
  isNameColVisible: boolean;
  isEmailColVisible: boolean;
  isJoinDateColVisible: boolean;
  isSignupCountryColVisible: boolean;
  isStatusColVisible: boolean;
  onUserToggle: (userId: number) => void;
  isUserSelected: boolean;
}

const UserListRow: React.FC<Props> = (props) => {
  const user = props.user;
  const userId = parseInt(user.id, 10);
  const t = (props as any).t;
  const dispatch = useDispatch();

  const organization = useSelector(authSelectors.selectedOrganizationKeyName);
  const isBeingDenied = useSelector((state: RootState) =>
    userSelectors.isDenyingUser(state, userId)
  );
  const isBeingApproved = useSelector((state: RootState) =>
    userSelectors.isApprovingUser(state, userId)
  );
  const isAcceptActionVisible = useSelector<RootState>((state) =>
    organizationSelectors.isUserListActionVisible(state, "accept")
  ) as boolean;

  const isRejectActionVisible = useSelector<RootState>((state) =>
    organizationSelectors.isUserListActionVisible(state, "reject")
  ) as boolean;

  const deny = () => dispatch(denyUserAction(organization, user.id));
  const approve = () => dispatch(approveUserAction(organization, user.id));
  const href = routes.userProfile(organization, user.id);

  const onAcceptBtnClick = () => {
    if (isBeingApproved) {
      return;
    }

    if (!window.confirm(`Are you sure that you want to accept ${user.name}`)) {
      return;
    }

    approve();
  };

  const onRejectBtnClick = () => {
    if (isBeingDenied) {
      return;
    }

    if (
      !window.confirm(`@todo Are you sure that you want to reject ${user.name}`)
    ) {
      return;
    }

    deny();
  };

  return (
    <TableRow data-test={TestIdsEnum.UserListRow}>
      {/*
      <td>
        <input
          type="checkbox"
          checked={props.isUserSelected}
          onChange={(e) => props.onUserToggle(userId)}
        />
      </td>
      */}
      {props.isNameColVisible && (
        <TableCellLink href={href}>
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textPrimary}
          >
            {user.name}
          </StyledText>
        </TableCellLink>
      )}
      {props.isEmailColVisible && (
        <TableCellLink href={href}>
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textPrimary}
          >
            {user.email}
          </StyledText>
        </TableCellLink>
      )}
      {props.isStatusColVisible && (
        <TableCellLink href={href}>
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textPrimary}
          >
            {user.status}
          </StyledText>
        </TableCellLink>
      )}
      {props.isJoinDateColVisible && (
        <TableCellLink href={href}>
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textPrimary}
          >
            {user.joinDate
              ? t("user_list.row.date", { date: moment(user.joinDate) })
              : "n/a"}
          </StyledText>
        </TableCellLink>
      )}
      {props.isSignupCountryColVisible && (
        <TableCellLink href={href}>
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textPrimary}
          >
            {user.signupCountry}
          </StyledText>
        </TableCellLink>
      )}
      <td className="text-right">
        <div className="btn-group">
          {isAcceptActionVisible && user.status !== UserStatus.Accepted && (
            <PrimaryButton
              color={colors.successDark}
              backgroundColor={colors.transparent}
              hoverColor={colors.successLight}
              onClick={() => onAcceptBtnClick()}
              className="mr-2"
              type="button"
            >
              Accept
            </PrimaryButton>
          )}
          {isRejectActionVisible && user.status !== UserStatus.Denied && (
            <PrimaryButton
              color={colors.errorDark}
              backgroundColor={colors.transparent}
              hoverColor={colors.errorLight}
              onClick={() => onRejectBtnClick()}
              className="mr-2"
              type="button"
            >
              Reject
            </PrimaryButton>
          )}
        </div>
      </td>
    </TableRow>
  );
};

export default withTranslation()(UserListRow);
